/**
 * Frontend-Portierung der Backend-Kalkulator-Formel.
 * Identische Zahlen wie /api/calculator/calculate — keine Latenz.
 *
 * Backend-Quelle: /app/backend/routes/calculator.py (calc_private / calc_business).
 * Bei Formel-Änderungen im Backend MUSS diese Datei mit-angepasst werden.
 */

// ── Constants (spiegeln backend/config/constants.py) ─────────────
// Neue Zwischenstufe 7.000–9.000 km = 109,90 € (Sweet-Spot-Preis für Wenig-Fahrer)
export const SILVER_CARD_PRICING = [
  { max_km: 5000,  price: 99.90 },
  { max_km: 7000,  price: 99.90 },   // 5.000–7.000 km bleibt bei 99,90 €
  { max_km: 9000,  price: 109.90 },   // 7.000–9.000 km = 109,90 € (neue Zwischenstufe)
  { max_km: 10000, price: 119.90 },   // 9.000–10.000 km bleibt bei 119,90 €
  { max_km: 15000, price: 139.90 },
  { max_km: 20000, price: 159.90 },
  { max_km: 30000, price: 169.90 },
  { max_km: 40000, price: 179.90 },
];
export const GOLD_CARD_PRICE = 49.90;

export const PRIVATE_MAX_VEHICLES = 1;
export const PRIVATE_MAX_KM = 40000;
export const BUSINESS_MAX_KM_PER_VEHICLE = 50000;
export const BUSINESS_FLEET_RATIO_THRESHOLD = 0.10;
export const BUSINESS_DIRECT_CHECKOUT_MAX = 200;
export const BUSINESS_MAX_VEHICLES = 5000;

export const AVG_CONSUMPTION_PER_100KM = 8.0;
export const AVG_FUEL_PRICE_PER_LITER = 1.75;
export const AVG_SAVINGS_PER_LITER = 0.40;

export const POINT_VALUE_EUR = 0.75;
export const POINTS_PER_KM = 0.1;
export const CREDIT_PER_KM_PRIVATE = 0.075;            // Einheitliche Rate (0,1 Punkte × 0,75 €)
export const CREDIT_PER_KM_PRIVATE_SWEETSPOT = 0.075;  // angeglichen — keine Sonder-Rate mehr
export const CREDIT_PER_KM_BUSINESS_SMALL = 0.075;
export const CREDIT_PER_KM_BUSINESS_LARGE = 0.075;

/** Einheitliche Guthaben-Rate 0,075 €/km für die Silver Card. */
export function creditPerKmPrivate() {
  return CREDIT_PER_KM_PRIVATE;
}

export const REDEMPTION_PER_BOOKING_PRIVATE = { min: 25, max: 250 };
export const REDEMPTION_PER_BOOKING_BUSINESS = { min: 25, max: 100 };

export const EXCLUDED_PRIVATE = ['Wohnmobile', 'Wohnwagen', 'Campervans'];
export const EXCLUDED_BUSINESS = ['LKW', 'Transporter', 'Taxis', 'Busse'];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

function getSilverPrice(annualKm) {
  for (const tier of SILVER_CARD_PRICING) {
    if (annualKm <= tier.max_km) return tier.price;
  }
  return SILVER_CARD_PRICING[SILVER_CARD_PRICING.length - 1].price;
}

export function calcPrivate({ vehicles = 1, annual_km = 15000 } = {}) {
  vehicles = clamp(vehicles, 1, PRIVATE_MAX_VEHICLES);
  const annualKm = clamp(annual_km, 5000, PRIVATE_MAX_KM);

  const totalKm = annualKm * vehicles;
  const liters = Math.round(totalKm * AVG_CONSUMPTION_PER_100KM / 100);
  const fuelCost = Math.round(liters * AVG_FUEL_PRICE_PER_LITER);
  const savingsAtPump = Math.round(liters * AVG_SAVINGS_PER_LITER);
  const creditRate = creditPerKmPrivate(annualKm);  // einheitliche Rate 0,075 €/km
  const tankCredit = Math.round(totalKm * creditRate);
  const cardFee = getSilverPrice(annualKm);

  const r = REDEMPTION_PER_BOOKING_PRIVATE;
  const maxBookingsMin = tankCredit >= r.max ? Math.max(1, Math.floor(tankCredit / r.max))
    : (tankCredit >= r.min ? 1 : 0);
  const maxBookingsMax = tankCredit >= r.min ? Math.max(1, Math.floor(tankCredit / r.min)) : 0;

  return {
    customer_type: 'private',
    card_type: 'silver',
    card_fee: cardFee,
    vehicles,
    annual_km_per_vehicle: annualKm,
    total_km: totalKm,
    liters,
    fuel_cost: fuelCost,
    savings_at_pump: savingsAtPump,
    tank_credit: tankCredit,
    tank_credit_points: Math.round(tankCredit / POINT_VALUE_EUR),
    credit_per_km: creditRate,
    redemption_per_booking: r,
    possible_bookings: { min: maxBookingsMin, max: maxBookingsMax },
    roi: cardFee > 0 ? Math.round((tankCredit / cardFee) * 10) / 10 : 0,
    validity_months: 12,
    pricing_tiers: SILVER_CARD_PRICING,
    max_vehicles: PRIVATE_MAX_VEHICLES,
    max_km: PRIVATE_MAX_KM,
    excluded_vehicles: EXCLUDED_PRIVATE,
  };
}

export function calcBusiness({ vehicles = 1, annual_km = 20000, employees = 0 } = {}) {
  vehicles = clamp(vehicles, 1, BUSINESS_MAX_VEHICLES);
  const annualKm = clamp(annual_km, 5000, BUSINESS_MAX_KM_PER_VEHICLE);
  employees = Math.max(vehicles, employees);

  const totalKm = annualKm * vehicles;
  const liters = Math.round(totalKm * AVG_CONSUMPTION_PER_100KM / 100);
  const fuelCost = Math.round(liters * AVG_FUEL_PRICE_PER_LITER);
  const savingsAtPump = Math.round(liters * AVG_SAVINGS_PER_LITER);

  const creditPerKm = vehicles >= 11 ? CREDIT_PER_KM_BUSINESS_LARGE : CREDIT_PER_KM_BUSINESS_SMALL;
  let tankCredit = Math.round(totalKm * creditPerKm);

  const fleetRatio = employees > 0 ? vehicles / employees : 1.0;
  // Einheitliche Formel wie Kasse/Backend: km × Fahrzeuge × 0,075 € — keine Mitarbeiter-Reduktion
  const reductionFactor = 1.0;

  const cardFee = Math.round(GOLD_CARD_PRICE * vehicles * 100) / 100;
  const r = REDEMPTION_PER_BOOKING_BUSINESS;
  const maxBookingsMin = tankCredit >= r.max ? Math.max(1, Math.floor(tankCredit / r.max))
    : (tankCredit >= r.min ? 1 : 0);
  const maxBookingsMax = tankCredit >= r.min ? Math.max(1, Math.floor(tankCredit / r.min)) : 0;

  return {
    customer_type: 'business',
    card_type: 'gold',
    card_fee: cardFee,
    price_per_vehicle: GOLD_CARD_PRICE,
    vehicles,
    employees,
    annual_km_per_vehicle: annualKm,
    total_km: totalKm,
    liters,
    fuel_cost: fuelCost,
    savings_at_pump: savingsAtPump,
    credit_per_km: creditPerKm,
    tank_credit: tankCredit,
    tank_credit_points: Math.round(tankCredit / POINT_VALUE_EUR),
    fleet_ratio: Math.round(fleetRatio * 1000) / 10,
    reduction_factor: reductionFactor,
    reduction_active: reductionFactor < 1.0,
    redemption_per_booking: r,
    possible_bookings: { min: maxBookingsMin, max: maxBookingsMax },
    roi: cardFee > 0 ? Math.round((tankCredit / cardFee) * 10) / 10 : 0,
    validity_months: 12,
    requires_inquiry: vehicles > BUSINESS_DIRECT_CHECKOUT_MAX,
    max_km_per_vehicle: BUSINESS_MAX_KM_PER_VEHICLE,
    excluded_vehicles: EXCLUDED_BUSINESS,
  };
}

/**
 * Universeller Frontend-Kalkulator — identisch zu POST /api/calculator/calculate.
 * Nutze diese Funktion überall wo Preise/Guthaben live berechnet werden müssen.
 */
export function calculateLocal({ customer_type = 'private', vehicles = 1, annual_km = 15000, employees = 0 } = {}) {
  if (customer_type === 'business') return calcBusiness({ vehicles, annual_km, employees });
  return calcPrivate({ vehicles, annual_km });
}
